import type { Logger } from "@expert/logging";
import type { Channel } from "@expert/shared-types";

import { useCallback, useEffect, useEffectEvent, useLayoutEffect, useMemo, useRef, useState } from "react";
import useWebSocket from "react-use-websocket";

import type {
    GaiaShadowConfig,
    ShadowComparison,
    ShadowMessage,
    ShadowSource,
    ShadowTallySummary,
} from "../shadow/types";
import type {
    GaiaSubscription,
    GaiaSubscriptionBody,
    GaiaUnsubscription,
    GaiaWebSocketResponse,
    GaiaWebsocketContext,
    OutboundMessageTransform,
    SendJsonMessage,
} from "../types";

import { getGaiaPartner } from "../../features";
import { useGaiaStore } from "../../store";
import { type ControlMessage, subscriptionKey } from "../controlMessages";
import { GAIA_WS_EMITTER_NAME, gaiaWsEventBus } from "../gaiaEventBus";
import { reportShadowComparison, reportShadowSummary } from "../shadow/report";
import { ShadowComparator } from "../shadow/ShadowComparator";
import { ShadowTally } from "../shadow/ShadowTally";
import { useGaiaShadowWebsocket } from "../shadow/useGaiaShadowWebsocket";
import { GaiaWebSocketEvent } from "../types";
import { useGaiaStatusEvents } from "./useGaiaStatusEvents";

const heartbeatInterval = 20000;
const heartbeatTimeout = 90000;
// Cap sends buffered while this connection hasn't finished its auth handshake, so a
// backend that never authorizes cannot grow the buffer without bound over a long session.
const maxSendBuffer = 200;

interface GaiaWebsocket {
    baseUrl: string;
    application: string;
    token: string;
    identity: string;
    logger: Logger;
    shadow?: GaiaShadowConfig;
    /** Applied to each consumer-initiated message before it is sent on the live socket. */
    transformOutbound?: OutboundMessageTransform;
}

export function useGaiaWebsocketInternal({
    baseUrl,
    application,
    token,
    identity,
    logger: loggerProp,
    shadow,
    transformOutbound,
}: GaiaWebsocket) {
    const [loading, setLoading] = useState(true);
    const url = `${baseUrl}?application=${application}&token=${token}`;
    const logger = useMemo(() => loggerProp.child({ module: "useGaiaWebsocket", supportTeam: "solve" }), [loggerProp]);

    // --- Simplr shadow websocket (dark launch) ----------------------------------
    // When enabled, a second connection to the Simplr backend runs alongside GAIA and
    // every inbound message from both sockets is compared (or, for events that can never pair,
    // counted per side); differences are surfaced via `shadow.onComparison`, a session sample is
    // logged, and a periodic tally summary carries the counts. Nothing here touches the live UI path.
    const shadowEnabled = Boolean(shadow?.enabled && shadow.url);

    const onSummary = useEffectEvent((summary: ShadowTallySummary) => reportShadowSummary(summary, shadow?.onSummary));

    // Counts every comparison (equal included) and flushes a low-volume summary so EDP can compute a match rate.
    // Owned by the effect, not a memo: it starts an interval and is single-use once disposed, so a memoized
    // instance would leak from a discarded render and stay dead after a StrictMode effect replay.
    const tallyRef = useRef<ShadowTally | null>(null);
    useEffect(() => {
        if (!shadowEnabled) return;
        const tally = new ShadowTally({ onSummary });
        tallyRef.current = tally;
        return () => {
            tallyRef.current = null;
            tally.dispose();
        };
    }, [shadowEnabled]);

    const onReport = useEffectEvent((comparison: ShadowComparison) => {
        tallyRef.current?.record(comparison);
        reportShadowComparison(comparison, logger, shadow?.onComparison);
    });

    const onUncompared = useEffectEvent((source: ShadowSource, message: ShadowMessage) =>
        tallyRef.current?.recordDelivery(source, message),
    );

    const comparator = useMemo(
        // oxlint-disable-next-line react-hooks/rules-of-hooks
        () => (shadowEnabled ? new ShadowComparator({ report: onReport, onUncompared }) : null),
        [shadowEnabled],
    );
    useEffect(() => () => comparator?.dispose(), [comparator]);

    // True only while the shadow socket is actually connected. Gating comparison on this means a
    // shadow that never opens (e.g. an unconfirmed Simplr URL) cannot flood metrics/logs/analytics
    // with spurious `gaia-only` results — we only compare when both sides could realistically deliver.
    const shadowConnectedRef = useRef(false);

    const onPrimaryInbound = comparator
        ? (message: GaiaWebSocketResponse) => {
              if (shadowConnectedRef.current) comparator.record("gaia", message);
          }
        : undefined;
    // ----------------------------------------------------------------------------

    // Gate outbound sends behind this specific connection's own auth handshake. Resets on
    // every `onOpen` (fresh connect and every reconnect) and only flips true once *that*
    // connection's own `authorization-success` arrives
    const isAuthenticatedRef = useRef(false);
    const sendBufferRef = useRef<Parameters<SendJsonMessage>[]>([]);
    // Active subscriptions, replayed after every successful authorization. The live URL can change
    // mid-session (the GAIA -> Simplr cutover), which swaps the socket underneath consumers without
    // remounting them, so their own reconnect-aware resubscribe logic is not guaranteed to re-fire
    // on the new connection. Replaying from here makes the new socket inherit the subscriptions
    // unconditionally, the same way the shadow socket already does.
    const subscriptionsRef = useRef<Map<string, Parameters<SendJsonMessage>>>(new Map());

    // Reset this connection's auth state the moment the URL changes, synchronously and before
    // `react-use-websocket`'s own effect constructs the replacement socket. Without this the refs
    // still say "authenticated" while `sendJsonMessage` already targets a CONNECTING socket, so a
    // subscribe issued in that window is handed straight to the library, queued, and flushed on
    // open — landing on the wire immediately after `authorize` and before `authorization-success`,
    // which the orchestrator rejects by closing the connection.
    const connectedUrlRef = useRef(url);
    useLayoutEffect(() => {
        if (connectedUrlRef.current === url) return;
        connectedUrlRef.current = url;
        isAuthenticatedRef.current = false;
        sendBufferRef.current = [];
        setLoading(true);
    }, [url]);

    const { sendJsonMessage: sendJsonMessageToGaia, getWebSocket } = useWebSocket(
        url,
        {
            heartbeat: {
                interval: heartbeatInterval,
                timeout: heartbeatTimeout,
                message: JSON.stringify({
                    action: "heartbeat",
                    correlationId: identity,
                }),
            },
            shouldReconnect: (_closeEvent) => true,
            reconnectAttempts: 10,
            reconnectInterval: 2000,
            onOpen: () => {
                logger.info("GAIA | websocket open");
                // Send auth directly through the raw sender. Using the gated `sendJsonMessage`
                // here would deadlock because authentication hasn't completed yet.
                isAuthenticatedRef.current = false;
                setLoading(true);
                sendJsonMessageToGaia({
                    action: "authorize",
                    token,
                    correlationId: identity,
                });
                logger.debug("GAIA | authorize message sent");
            },
            onMessage: createOnMessageEventHandler({
                logger,
                onInbound: onPrimaryInbound,
                isAuthenticatedRef,
                onAuthorized: () => {
                    logger.info("GAIA | websocket authorized");
                    setLoading(false);
                    // Subscriptions first, so the backend has registered them before any buffered
                    // session-scoped traffic that assumes an active subscription arrives.
                    for (const args of subscriptionsRef.current.values()) sendJsonMessageToGaia(...args);
                    const buffered = sendBufferRef.current;
                    sendBufferRef.current = [];
                    for (const args of buffered) sendJsonMessageToGaia(...args);
                },
            }),
            onClose: (event: CloseEvent) => {
                // `react-use-websocket` closes a retired socket without unbinding its `onclose`, and
                // that handler reads the *current* render's options — so a socket replaced by a URL
                // change (the GAIA -> Simplr cutover) invokes this callback once its close handshake
                // completes, which can be after the replacement is already open and authorized.
                // Acting on it then would mark a live, authorized connection unauthenticated with
                // nothing left to ever clear it, wedging every send for the rest of the session.
                if (event?.target && event.target !== getWebSocket()) {
                    logger.debug("GAIA | ignoring close from a retired websocket");
                    return;
                }
                logger.info("GAIA | websocket closed");
                isAuthenticatedRef.current = false;
                // Drop buffered one-off sends rather than replay them: they were addressed to the
                // old connection and consumers re-issue what still matters. Subscriptions are the
                // exception — they live in `subscriptionsRef` and are replayed on the next auth.
                sendBufferRef.current = [];
                setLoading(true);
            },
            onError: (event: Event) => onError(event, logger),
        },
        true,
    );

    const { sendToShadow } = useGaiaShadowWebsocket({
        enabled: shadowEnabled,
        shadowUrl: shadow?.url,
        application,
        token,
        identity,
        comparator,
        connectedRef: shadowConnectedRef,
        logger: loggerProp,
    });

    // Mirror consumer-initiated messages (subscribe/unsubscribe/invoke/…) to the shadow
    // socket so it receives equivalent traffic. The shadow handles its own authorize.
    const shadowTransformOutbound = shadow?.transformOutbound;
    const sendJsonMessage = useCallback<SendJsonMessage>(
        (consumerMessage, keep) => {
            const message = transformOutbound ? transformOutbound(consumerMessage) : consumerMessage;
            // Subscribes/unsubscribes are tracked rather than buffered: they are the connection's
            // durable state, so they are held in `subscriptionsRef` and (re)sent on every
            // successful authorization instead of queued once and lost when a socket is replaced.
            const control = message as ControlMessage | null;
            const key = subscriptionKey(control);
            const isSubscribe = control?.action === "subscribe";
            const isTrackedSubscription = Boolean(key) && (isSubscribe || control?.action === "unsubscribe");

            if (isTrackedSubscription && key) {
                if (isSubscribe) {
                    subscriptionsRef.current.set(key, [message, keep]);
                } else {
                    subscriptionsRef.current.delete(key);
                }
            }

            if (isAuthenticatedRef.current) {
                sendJsonMessageToGaia(message, keep);
            } else if (!isTrackedSubscription) {
                sendBufferRef.current.push([message, keep]);
                if (sendBufferRef.current.length > maxSendBuffer) {
                    // Evict the oldest: a session's own control frames are tracked separately, so
                    // what is dropped here is the stalest one-off request.
                    sendBufferRef.current.shift();
                }
            }
            if (shadowEnabled) {
                try {
                    sendToShadow(
                        shadowTransformOutbound ? shadowTransformOutbound(consumerMessage) : consumerMessage,
                        keep,
                    );
                } catch (error: unknown) {
                    logger.warn({ error }, "GAIA | shadow mirror send failed");
                }
            }
        },
        [sendJsonMessageToGaia, shadowEnabled, sendToShadow, logger, transformOutbound, shadowTransformOutbound],
    );

    useGaiaStatusEvents(logger);

    const subscriptionResult: GaiaWebsocketContext = useMemo(() => {
        return {
            sendJsonMessage,
            loading,
            subscribeSessionToGaia: subscribeSessionToGaia(identity),
            unsubscribeSessionFromGaia: unsubscribeSessionFromGaia(identity),
        };
    }, [sendJsonMessage, loading, identity]);

    return subscriptionResult;
}

interface CreateOnMessageEventHandlerParams {
    logger: Logger;
    onInbound?: (message: GaiaWebSocketResponse) => void;
    isAuthenticatedRef: { current: boolean };
    onAuthorized: () => void;
}

const createOnMessageEventHandler =
    ({ logger: loggerProp, onInbound, isAuthenticatedRef, onAuthorized }: CreateOnMessageEventHandlerParams) =>
    (event: MessageEvent) => {
        try {
            const data = JSON.parse(event.data as string) as GaiaWebSocketResponse;

            // Guard against malformed messages
            if (!data?.name) {
                loggerProp.warn({ data }, "GAIA | Received message without name property");
                return;
            }

            // Feed the live message into the shadow comparator. Isolated in its own try/catch so a
            // fault in the observational dark-launch path can never interrupt live GAIA delivery.
            try {
                onInbound?.(data);
            } catch (error: unknown) {
                loggerProp.warn({ error }, "GAIA | shadow inbound hook failed");
            }

            const { body } = data;
            const logger = getLoggerContextFromBody(body, loggerProp);

            if (data.name.includes("error")) {
                const status = (body as { status?: number | string } | null)?.status;
                const isExpectedNotFound = status === 404 || status === "404";
                if (!isExpectedNotFound) {
                    logger.warn({ body, eventName: data.name }, "GAIA | message received");
                }
            } else {
                logger.debug({ body }, `GAIA | message received: ${GAIA_WS_EMITTER_NAME}_${data.name}`);
            }

            gaiaWsEventBus.emit(`${GAIA_WS_EMITTER_NAME}_${data.name}`, data);
            if (data.name === GaiaWebSocketEvent.AuthorizationSuccess && !isAuthenticatedRef.current) {
                isAuthenticatedRef.current = true;
                onAuthorized();
            }
        } catch (error: unknown) {
            // oxlint-disable-next-line typescript/no-misused-spread
            loggerProp.error({ error, ...event }, "GAIA | message handler error");
        }
    };

const onError = (event: Event, logger: Logger) => {
    logger.warn({ error: event }, "GAIA | websocket error");
};

export const subscribeSessionToGaia =
    (identity: string) =>
    ({
        chatId,
        sessionId,
        partner,
        sendJsonMessage,
        callSid,
        timeout = 5000,
        logger: loggerProp,
        explicitSubscriptions,
    }: GaiaSubscription) => {
        useGaiaStore.getState().setPartner(sessionId, partner);

        // Add partner, sessionId, and chatId/callSid to logger context for session-specific logs
        const logger = loggerProp.child({
            module: "useGaiaWebsocket",
            sessionId,
            partner,
            ...(chatId && { chatId }),
            ...(callSid && { callSid }),
        });

        return new Promise<void>((resolve, reject) => {
            if (chatId) {
                useGaiaStore.getState().setSessionType(sessionId, "chat");
            } else if (callSid) {
                useGaiaStore.getState().setSessionType(sessionId, "voice");
            }

            sendJsonMessage({
                action: "subscribe",
                sessionGroupId: sessionId,
                sessionId: chatId ?? callSid ?? sessionId,
                explicitSubscriptions,
                partner: getGaiaPartner(partner),
                correlationId: identity,
            });
            logger.debug("GAIA | session subscribing");

            const timer = setTimeout(() => {
                cleanupEventListener();
                const message = "GAIA | session subscription timed out";
                logger.warn(message);
                reject(Error(message));
            }, timeout);

            const cleanupEventListener = gaiaWsEventBus.once("gaia_ws_session-subscription-success", () => {
                clearTimeout(timer);
                resolve();
            });
        });
    };

export const unsubscribeSessionFromGaia =
    (identity: string) =>
    ({ sessionId, callSid, chatId, sendJsonMessage, logger }: GaiaUnsubscription) => {
        const partner = useGaiaStore.getState().partners[sessionId];

        // Mirror subscribe's addressing: the backend registered this subscription under
        // `chatId ?? callSid ?? sessionId`, so we must unsubscribe by that same key.
        // Sending only the workspace sessionId leaves call/chat subscriptions alive (leak).
        sendJsonMessage({
            action: "unsubscribe",
            correlationId: identity,
            sessionGroupId: sessionId,
            sessionId: chatId ?? callSid ?? sessionId,
        });

        logger
            .child({
                sessionId,
                ...(chatId && { chatId }),
                ...(callSid && { callSid }),
                module: "useGaiaWebsocket",
                partner,
            })
            .debug("GAIA | session unsubscribing");
    };

const getLoggerContextFromBody = (body: unknown, logger: Logger) => {
    if (!body) return logger;

    const { sessionGroupId, sessionId: sessionIdFromBody } = body as Partial<GaiaSubscriptionBody>;
    if (sessionGroupId ?? sessionIdFromBody) {
        /* The sessionGroupId is a term used by GAIA. The value being used is the sessionId.
                In the event there is a call or chat, sessionId is the callSid or chatId respectively. It's stupid, we know. */

        const isThereACallSidOrChatId = sessionGroupId && sessionIdFromBody && sessionIdFromBody !== sessionGroupId;
        const callSidOrChatId = isThereACallSidOrChatId ? sessionIdFromBody : undefined;
        const sessionId = sessionGroupId ?? sessionIdFromBody;
        const partner = sessionId ? useGaiaStore.getState().partners[sessionId] : undefined;

        const sessionType = sessionId ? getSessionType(sessionId) : undefined;
        const isChat = sessionType === "chat";

        return logger.child({
            sessionId,
            ...(callSidOrChatId && isChat
                ? { chatId: callSidOrChatId }
                : callSidOrChatId
                  ? { callSid: callSidOrChatId }
                  : {}),
            partner,
        });
    }

    return logger;
};

export function getSessionType(sessionId: string): Channel | undefined {
    return useGaiaStore.getState().getSessionType(sessionId);
}
